import { Alert, Button, Field, Input } from '@personachat/ui';
import { Download, ShieldCheck } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import { messageDe } from '../../api/call';
import { activerDoubleAuth, preparerDoubleAuth } from '../../auth/session';
import { Code } from '../../components/bits';
import { enregistrer, nomDate } from '../../lib/fichiers';

import { QrCode } from './QrCode';

/**
 * Champ du code à 6 chiffres de l'application (ou d'un code de secours
 * XXXX-XXXX) et son bouton. Le message de l'API s'affiche en cas d'échec.
 */
export function FormulaireCode({
  libelle = 'Code à 6 chiffres',
  aide,
  bouton,
  onValider,
  autoFocus = true,
}: {
  libelle?: string;
  aide?: string;
  bouton: string;
  onValider: (code: string) => Promise<void>;
  autoFocus?: boolean;
}) {
  const id = useId();
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    if (envoi || code.trim().length < 6) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await onValider(code.trim());
    } catch (err) {
      setErreur(messageDe(err));
      setCode('');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="stack" onSubmit={valider} noValidate>
      {erreur && <Alert>{erreur}</Alert>}
      <Field id={`${id}-code`} label={libelle} hint={aide}>
        <Input
          id={`${id}-code`}
          className="saisie-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="text"
          autoComplete="one-time-code"
          spellCheck={false}
          maxLength={20}
          autoFocus={autoFocus}
        />
      </Field>
      <Button type="submit" variant="primary" block loading={envoi} disabled={code.trim().length < 6}>
        {bouton}
      </Button>
    </form>
  );
}

/** Codes de secours, montrés une seule fois : à télécharger ou copier. */
export function CodesSecours({ codes }: { codes: string[] }) {
  const telecharger = () => {
    const texte = [
      'MyBizGame IA — back-office',
      'Codes de secours de la double authentification (chacun ne sert qu’une fois) :',
      '',
      ...codes,
      '',
    ].join('\n');
    enregistrer(new Blob([texte], { type: 'text/plain;charset=utf-8' }), nomDate('codes-secours-back-office', 'txt'));
  };
  return (
    <div className="stack">
      <ul className="codes-secours" aria-label="Codes de secours">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
      <div className="row">
        <Button size="sm" icon={<Download size={14} aria-hidden="true" />} onClick={telecharger}>
          Télécharger
        </Button>
        <Code valeur={codes.join(' ')} />
      </div>
    </div>
  );
}

/**
 * Activation de la double authentification (cahier des charges §9) :
 * 1. un secret est créé et affiché en QR code ;
 * 2. le premier code de l'application l'active ;
 * 3. les codes de secours s'affichent une seule fois.
 * Sert à l'enrôlement obligatoire (écran de connexion) et à « Mon compte ».
 */
export function Enrolement({ onTermine, remplacement = false }: { onTermine: () => void; remplacement?: boolean }) {
  const [secret, setSecret] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [notes, setNotes] = useState(false);

  const commencer = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      setSecret(await preparerDoubleAuth());
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  if (codes) {
    return (
      <div className="stack">
        <Alert tone="info">
          Double authentification {remplacement ? 'reliée au nouveau téléphone' : 'activée'}. Notez ces codes de secours : chacun remplace
          une fois le code de l’application si vous perdez votre téléphone. Ils ne seront plus affichés.
        </Alert>
        <CodesSecours codes={codes} />
        <label className="case">
          <input type="checkbox" checked={notes} onChange={(e) => setNotes(e.target.checked)} />
          J’ai noté mes codes de secours en lieu sûr
        </label>
        <Button variant="primary" block disabled={!notes} onClick={onTermine}>
          Continuer
        </Button>
      </div>
    );
  }

  if (!secret) {
    return (
      <div className="stack">
        {erreur && <Alert>{erreur}</Alert>}
        <p className="texte-aide">
          Il vous faut une application d’authentification sur votre téléphone (Google Authenticator, Microsoft Authenticator, 1Password…). À
          chaque connexion, elle donnera un code à 6 chiffres en plus du mot de passe.
        </p>
        <Button variant="primary" block icon={<ShieldCheck size={15} aria-hidden="true" />} loading={envoi} onClick={() => void commencer()}>
          {remplacement ? 'Relier un nouveau téléphone' : 'Activer la double authentification'}
        </Button>
      </div>
    );
  }

  return (
    <div className="stack">
      {erreur && <Alert>{erreur}</Alert>}
      <ol className="etapes">
        <li>Dans l’application, ajoutez un compte et scannez ce QR code.</li>
      </ol>
      <div className="qr-bloc">
        <QrCode valeur={secret.otpauthUrl} libelle="QR code à scanner avec l’application d’authentification" />
        <div className="qr-bloc__secret">
          <span className="muted small">Pas de caméra ? Saisissez cette clé :</span>
          <Code valeur={secret.secret.replace(/(.{4})/g, '$1 ').trim()} />
        </div>
      </div>
      <ol className="etapes" start={2}>
        <li>Saisissez le code à 6 chiffres affiché par l’application.</li>
      </ol>
      <FormulaireCode
        bouton="Vérifier et activer"
        autoFocus={false}
        onValider={async (code) => {
          setCodes(await activerDoubleAuth(code));
        }}
      />
    </div>
  );
}
